import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { addToastAction } from '@bottomless/common/store';
import { Categories } from '@bottomless/common/constants';
import { ProductRequestForm } from '../components/ProductRequestForm';
import { RequestSuccessMessage } from '../components/RequestSuccessMessage';
import { saveReferralPilotAction } from '../../../store';
import { Alert, Container } from 'reactstrap';
import { HowItWorks } from '../components/HowItWorks';
import { Reviews } from '../components/Reviews';
import { Faq } from '../components/Faq';
import './PetFoodInvitation.scss';

const faqs = [
  {
    question: "How does Bottomless' Subscribe By Usage work?",
    answer:
      "When you enter your email, our team will be in touch to set you up with your pet's choice of food. You will also receive a smart reordering scale. Sync it to your Wi-Fi network once, then leave your pet food on it. The scale will learn from your pet&apos;s consumption and reorder at just the right time.",
  },
  {
    question: 'How much does the pet food cost?',
    answer: 'We match the the price of any pet food seller that ships to your address.',
  },
  {
    question: 'How much does the service cost?',
    answer:
      "Beta testers get a scale at no extra cost with their first order. We don't charge a service fee for the pet food beta, you only pay for the pet food.",
  },
  {
    question: 'Do customers have control over ordering?',
    answer: 'Yes, you will get an SMS or email alert 8-12 hours before reorders are placed.',
  },
  {
    question: 'Can I use my own container with the scale?',
    answer: "Yes! The scale can easily be 'zeroed' with almost any container or tub.",
  },
];

const howItWorks = [
  {
    image: 'https://bottomless.imgix.net/landing-pets-v2/how-it-works-1.png',
    title: "Choose your pet's food",
    description: 'We will need a product link to set up your subscription by usage',
  },
  {
    image: 'https://bottomless.imgix.net/landing-pets-v2/how-it-works-2.png',
    title: 'Set up your scale',
    description: 'Receive and set up the Wi-Fi scale. Then, store your pet food on it',
  },
  {
    image: 'https://bottomless.imgix.net/landing-pets-v2/how-it-works-3.png',
    title: 'Get reorders based on usage',
    description: 'The scale will reorder fresh pet food at the perfect time',
  },
];

const valueProps = [
  {
    title: 'Any brand',
    description: 'Use Subscribe By Usage with any pet food brand',
  },
  {
    title: 'Works with containers',
    description: 'Use the scale with your own container',
  },
  {
    title: '5+ scale sizes',
    description: 'Fits snacks and pet food of different sizes',
  },
];

const reviews = [
  {
    text: 'I appreciate you guys alot!! Makes my life easier.',
    author: 'Violet',
    source: 'Pet food customer with 2 scales',
    image: 'https://bottomless.imgix.net/landing-pets-v2/review-1.png',
  },
  {
    text: 'This auto renewal is fantastic! Thanks so much! Timing was so spot on too!',
    author: 'Johnathon',
    source: 'Pet food customer',
    image: 'https://bottomless.imgix.net/landing-pets-v2/review-2.png',
  },
  {
    text:
      "Bottomless has helped make it so easy to keep my kitties fed, now I'm happy to add that 2nd scale for my new cat, thanks!",
    author: 'Eric',
    source: 'Pet food customer with 2 scales',
    image: 'https://bottomless.imgix.net/landing-pets-v2/review-3.png',
  },
  {
    text:
      "Welp, I can say with 100% confidence this works better than Amazon Subscribe & Save. Delivered right about the time we'd start forgetting to buy/order another bag.",
    author: 'Ian',
    source: 'Pet food customer',
    image: 'https://bottomless.imgix.net/landing-pets-v2/review-4.png',
  },
];

const PetFoodInvitationComponent = ({ referrer, saveReferralPilot, addToast }) => {
  const [hasSubmittedRequest, setHasSubmittedRequest] = useState(false);

  const descriptionPlaceholder = `Example:
Purina Pro Plan High Protein Dog Food With Probiotics for Dogs
or
(https://www.amazon.com/Purina-Pro-Plan-Shredded-Chicken/dp/B000084E61)`;

  const handleProductRequestSubmit = useCallback(
    async data => {
      try {
        const response = (await saveReferralPilot(data)).payload;

        if (!response || response?.error) {
          setHasSubmittedRequest(false);
          addToast('There was an error submitting your request.', 'danger');
          return;
        }

        setHasSubmittedRequest(true);
      } catch (error) {
        setHasSubmittedRequest(false);
        addToast('There was an error submitting your request.', 'danger');
      }
    },
    [saveReferralPilot, addToast]
  );

  return (
    <>
      <div className="splash-wrapper section section-splash section-white">
        <Container>
          <div className="referral-pet-invite">
            <h1 className="mb-4 text-center text-md-left">
              Hey! Your friend <span className="text-primary font-italic">{referrer.name}</span>
              <br />
              thinks you&apos;ll love Bottomless
            </h1>

            <Alert color="primary text-body py-4 text-center text-md-left">
              <div className="font-weight-bold alert-title mb-2">
                Join now to get 30% off your first pet food purchase
              </div>
              <div>And a free Bottomless Pet Food Subscription Membership!</div>
            </Alert>

            <p className="text-center text-md-left">
              Please provide a link or description for the product you&apos;d like to link. We&apos;ll add it and notify
              you.
            </p>

            <div className="hero-wrapper referral-invite-container">
              <div className="referral-container-generic">
                <div className="request-container">
                  {hasSubmittedRequest ? (
                    <RequestSuccessMessage pilotName="Pet Food" />
                  ) : (
                    <ProductRequestForm
                      onSubmit={handleProductRequestSubmit}
                      referral_id={referrer.referral_id}
                      pilotName="Pet Food"
                      pilotCategory={Categories.PetFood}
                      placeholder={descriptionPlaceholder}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <HowItWorks howItWorks={howItWorks} />
      <Reviews valueProps={valueProps} reviews={reviews} />
      <Faq faqs={faqs} />
    </>
  );
};

PetFoodInvitationComponent.propTypes = {
  referrer: PropTypes.object.isRequired,
  saveReferralPilot: PropTypes.func.isRequired,
  addToast: PropTypes.func.isRequired,
};

export const PetFoodInvitation = withRouter(
  connect(
    ({ user }) => ({ me: user.me }),
    dispatch => ({
      saveReferralPilot: data => dispatch(saveReferralPilotAction(data)),
      addToast: message => dispatch(addToastAction(message)),
    })
  )(PetFoodInvitationComponent)
);
